import * as api from '../../api/client'
import { keychain } from '../../api/keychain'
import { bridge } from '../../bridge'
import { useAppStore } from '../../store/app'
import { browserDeviceLabel } from '../../util/browserName'
import { randomUUID } from '../../util/uuid'

// Two-stage enrollment flow ported from HearthService. Stage one
// asks the server to email an OTP; stage two verifies the OTP,
// optionally prompts for display names (when the server reports
// is_new_user), and enrolls the device.
//
// Split so the RegistrationScreen can slot a "Your name / Organization
// name" step between OTP verification and enrollment for first-time
// users. verifyCode returns the session token + is_new_user flag;
// finishEnroll consumes the session token along with the chosen names.

export async function requestCode(email: string): Promise<void> {
  await api.requestAuthCode(email)
}

export async function verifyCode(args: {
  email: string
  code: string
}): Promise<{ sessionToken: string; isNewUser: boolean; hasPendingInvites: boolean }> {
  return api.verifyAuthCode(args.email, args.code, 'enroll_device')
}

/// Enrolls the device. Returns any invitations the server found for the
/// verified email — it resolves them itself rather than being handed a
/// token, so a user who was invited but never opened the link still gets
/// the offer. When the list is non-empty the server also declines to mint
/// a bootstrap household, leaving organization_id empty so App.tsx routes
/// to OrphanedScreen, where the choice lives.
export async function finishEnroll(args: {
  email: string
  sessionToken: string
  userName?: string
  organizationName?: string
}): Promise<void> {
  const existing = await keychain.ioDeviceID()
  const mode: 'fresh' | 'reclaim' = existing ? 'reclaim' : 'fresh'
  const ioDeviceID = existing ?? randomUUID()

  const { platform } = await bridge.call('app.info')
  // Pick the right attestation source per platform. iOS = DeviceCheck JWT;
  // Android = Play Integrity token; web has no browser equivalent so
  // attestationToken stays null and the server falls through to its
  // type=none envelope handling (gated on the `dev` build tag — prod web
  // trust policy is TBD per docs/web-as-product-surface.md).
  let attestationToken: string | null = null
  let isSandbox = false
  if (platform === 'android') {
    const r = await api.playIntegrityToken()
    attestationToken = r.supported ? r.token : null
  } else if (platform === 'ios') {
    const r = await api.deviceCheckToken()
    attestationToken = r.supported ? r.token : null
    isSandbox = r.isSandbox
  }
  // platform === 'web': leave both null.

  try {
    // Device name must be unique per (owner, name) — schema enforces it.
    // Suffix with the short form of the io_device_id so each fresh
    // enrollment on the same account gets a distinct row even if the
    // user has previously registered other phones under the same login.
    const suffix = ioDeviceID.slice(0, 8)
    // For native: a coarse label that the OS implies; for web: parse the
    // UA so "Chrome on macOS" + "Safari on iPhone" are distinguishable
    // in the user's device list. User can rename via the device-list UI.
    const deviceLabel =
      platform === 'android'
        ? 'Android'
        : platform === 'web'
          ? browserDeviceLabel()
          : 'iPhone'
    const res = await api.enrollDevice({
      sessionToken: args.sessionToken,
      ioDeviceID,
      mode,
      deviceName: mode === 'reclaim' ? deviceLabel : `${deviceLabel} (${suffix})`,
      // form_factor='browser' for web tier so the io_device row is
      // honestly classified and the auto-prune sweeper can find it.
      // Native shells still classify as 'phone' (tablet detection isn't
      // wired today; the schema accepts 'tablet' but the UX hasn't
      // pressed on it).
      formFactor: platform === 'web' ? 'browser' : 'phone',
      attestationToken,
      isSandbox,
      platform,
      // Pass names through unconditionally. Server's
      // maybeApplyEnrollmentNaming no-ops on already-named accounts, so
      // reclaim flows that smuggle values through are harmless.
      userName: args.userName?.trim() || undefined,
      organizationName: args.organizationName?.trim() || undefined,
    })

    const normalizedEmail = args.email.trim().toLowerCase()
    await keychain.setIoDeviceID(res.io_device_id)
    await keychain.setIoDeviceSecret(res.device_secret)
    await keychain.setHumanUserID(res.human_user_id)
    await keychain.setOrganizationID(res.organization_id)
    await keychain.setEmail(normalizedEmail)

    const store = useAppStore.getState()
    store.setRegistered({
      humanUserID: res.human_user_id,
      organizationID: res.organization_id,
      ioDeviceID: res.io_device_id,
      email: normalizedEmail,
    })
    // Seed the invitation offer before the WebSocket exists, so a brand-new
    // invitee's first screen already shows who asked them to join rather
    // than a bare "No household".
    if (res.pending_invites?.length) {
      store.replaceMyInvites(res.pending_invites)
    }

    // Push registration is deferred until the org is server-confirmed
    // Pro — the store's replaceOrganizations action fires
    // apns.registerForPush when the current org's is_pro flips false→
    // true. That covers re-login (already-Pro) and offer-code/sub
    // claim from a single trigger.
  } catch (err) {
    if (mode === 'reclaim') {
      await keychain.wipeDeviceCreds()
    }
    throw err
  }
}

/// Default user-name suggestion derived from an email address.
/// "matt.beller@..." → "Matt Beller". Used by the Registration screen
/// to pre-fill the name input. The org-name suggestion is derived from
/// whatever the user actually has in the name field via
/// defaultOrgNameFromUserName, not from the email — typing your real
/// name should change the suggested household name.
export function defaultUserNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[._\-+]/).filter((p) => p.length > 0)
  if (parts.length === 0) return 'User'
  return parts
    .map((p) => p[0].toUpperCase() + p.slice(1).toLowerCase())
    .join(' ')
}

/// "Matt Beller" → "Matt's Household". Uses the first whitespace-
/// separated token so a full name suggests a household scoped to the
/// person's first name.
export function defaultOrgNameFromUserName(userName: string): string {
  const trimmed = userName.trim()
  if (trimmed === '') return ''
  const first = trimmed.split(/\s+/)[0]
  return `${first}'s Household`
}
